import { type Readable, type Signal, signal, untrack, watch } from '@vielzeug/ripple';

// ── syncedSignal ──────────────────────────────────────────────────────────────

/**
 * Creates a local writable `Signal<T>` that stays in sync with an external
 * `Reactive<T>`. When `abortSignal` is provided the watcher subscription
 * is disposed automatically on abort; otherwise the caller is responsible for
 * disposal.
 *
 * Initialised synchronously via `untrack` so the first render never reads a
 * stale value. The optional `transform` coerces each incoming value (e.g.
 * `Boolean`, `String`).
 *
 * @example
 * ```ts
 * const value = syncedSignal(options.value, options.signal, (v) => String(v ?? ''));
 * // Without lifecycle signal (test / non-component context):
 * const value = syncedSignal(source);
 * ```
 */
export const syncedSignal = <TIn, TOut = TIn>(
  source: Readable<TIn>,
  abortSignal?: AbortSignal,
  transform: (value: TIn) => TOut = (v) => v as unknown as TOut,
): Signal<TOut> => {
  const local = signal<TOut>(untrack(() => transform(source.value)));
  const sub = watch(source, (v) => {
    local.value = transform(v);
  });

  if (abortSignal) {
    abortSignal.addEventListener('abort', () => sub.dispose(), { once: true });
  }

  return local;
};
